import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, Users, MessageSquare, CheckCircle2, TrendingUp, MapPin } from "lucide-react";

export const Route = createFileRoute("/dashboard")({
  head: () => ({ meta: [{ title: "Xamseen — Civic Intelligence" }] }),
  component: DashboardPage,
});

type Stats = { posts: number; resolved: number; communities: number; members: number; comments: number; geo: number };

function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [byCategory, setByCategory] = useState<{ category: string; count: number }[]>([]);

  useEffect(() => {
    (async () => {
      const [p, r, c, m, cm, g] = await Promise.all([
        supabase.from("posts").select("id", { count: "exact", head: true }),
        supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "resolved"),
        supabase.from("communities").select("id", { count: "exact", head: true }),
        supabase.from("community_members").select("user_id", { count: "exact", head: true }),
        supabase.from("comments").select("id", { count: "exact", head: true }),
        supabase.from("posts").select("id", { count: "exact", head: true }).not("latitude", "is", null),
      ]);
      setStats({
        posts: p.count ?? 0, resolved: r.count ?? 0, communities: c.count ?? 0,
        members: m.count ?? 0, comments: cm.count ?? 0, geo: g.count ?? 0,
      });
      const { data } = await supabase.from("posts").select("category");
      const counts: Record<string, number> = {};
      (data ?? []).forEach((d: any) => { counts[d.category] = (counts[d.category] ?? 0) + 1; });
      setByCategory(Object.entries(counts).map(([category, count]) => ({ category, count })).sort((a,b)=>b.count-a.count));
    })();
  }, []);

  const cards = [
    { label: "Total reports", value: stats?.posts ?? 0, icon: BarChart3, color: "text-primary" },
    { label: "Resolved", value: stats?.resolved ?? 0, icon: CheckCircle2, color: "text-secondary" },
    { label: "Communities", value: stats?.communities ?? 0, icon: Users, color: "text-primary" },
    { label: "Members", value: stats?.members ?? 0, icon: TrendingUp, color: "text-secondary" },
    { label: "Discussions", value: stats?.comments ?? 0, icon: MessageSquare, color: "text-primary" },
    { label: "Mapped reports", value: stats?.geo ?? 0, icon: MapPin, color: "text-secondary" },
  ];

  return (
    <AppShell>
      <div className="space-y-6">
        <header>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Civic Intelligence</h1>
          <p className="text-muted-foreground">Community activity, geographic insights and resolution tracking.</p>
        </header>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.label} className="p-4">
                <Icon className={`mb-2 h-5 w-5 ${c.color}`} />
                <div className="text-2xl font-semibold">{c.value.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">{c.label}</div>
              </Card>
            );
          })}
        </div>

        <Card className="p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">Reports by category</h2>
          <div className="space-y-3">
            {byCategory.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
            {byCategory.map((row) => {
              const max = Math.max(...byCategory.map((r) => r.count));
              const pct = (row.count / max) * 100;
              return (
                <div key={row.category}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="capitalize">{row.category}</span>
                    <span className="text-muted-foreground">{row.count}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
